import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

/*
 * OrgContext — current-organization state for the app.
 *
 * Wraps Dashboard so every component inside knows which org is active and
 * can subscribe to changes. The provider:
 *   • picks an initial org (localStorage if still valid, else first membership)
 *   • keeps axios's default X-Org-Id header in sync with the selection
 *   • offers switchOrg(orgId) which updates state and hard-reloads
 *
 * Hard reload on switch is intentional: it's the simplest way to flush every
 * cached query result (projects, users, etc.) so there's no risk of cross-org
 * data flashing into view.
 */

const OrgContext = createContext(null);

export function OrgProvider({ memberships = [], children }) {
  const [currentOrgId, setCurrentOrgId] = useState(null);

  // Pick the initial current org. Runs when memberships arrive.
  useEffect(() => {
    if (memberships.length === 0) return;
    if (currentOrgId && memberships.some(m => m.organization._id === currentOrgId)) {
      return; // already valid
    }

    const stored = localStorage.getItem('currentOrgId');
    const storedIsValid = stored && memberships.some(m => m.organization._id === stored);
    setCurrentOrgId(storedIsValid ? stored : memberships[0].organization._id);
  }, [memberships, currentOrgId]);

  // Keep axios + localStorage in sync with the selection.
  useEffect(() => {
    if (currentOrgId) {
      axios.defaults.headers.common['X-Org-Id'] = currentOrgId;
      localStorage.setItem('currentOrgId', currentOrgId);
    } else {
      delete axios.defaults.headers.common['X-Org-Id'];
      localStorage.removeItem('currentOrgId');
    }
  }, [currentOrgId]);

  const currentMembership =
    memberships.find(m => m.organization._id === currentOrgId) || null;
  const currentOrg = currentMembership?.organization || null;

  const switchOrg = (orgId) => {
    if (orgId === currentOrgId) return;
    setCurrentOrgId(orgId);
    // Hard reload to flush any in-memory state from the previous org.
    setTimeout(() => window.location.reload(), 0);
  };

  return (
    <OrgContext.Provider value={{
      memberships,
      currentOrg,
      currentMembership,
      switchOrg,
    }}>
      {children}
    </OrgContext.Provider>
  );
}

export const useOrg = () => {
  const ctx = useContext(OrgContext);
  if (!ctx) {
    // Soft default for components rendered before the provider mounts.
    return { memberships: [], currentOrg: null, currentMembership: null, switchOrg: () => {} };
  }
  return ctx;
};
